import React, { useState, useEffect } from "react";
import { Modal, Button, Form, InputGroup, Tabs, Tab } from "react-bootstrap";

const rolesDefault = ["User", "Approver Level 1", "Approver Level 2", "Manager", "Admin"];
const statuses = ["Active", "Inactive", "Locked"];
const positions = [
  { value: 0, label: "Staff" },
  { value: 1, label: "Department Manager" },
  { value: 2, label: "Division Manager" },
  { value: 3, label: "Board of Directors" },
   { value: 4, label: "Tradesperson" }
];

const PASSWORD_PLACEHOLDER = "********";

const UserModal = ({ show, onClose, onSave, initialData, serverError, branches, departments }) => {
  const [key, setKey] = useState("account");
  const [showPassword, setShowPassword] = useState(false);
  const [validated, setValidated] = useState(false);

  const [formData, setFormData] = useState({
    id: "", userName: "", fullName: "", email: "", status: "Active", role: "User",
    password: "", branchId: "", departmentIds: [], position: 0,
    employeeCode: "", positionDescription: "", workLocation: "",
    currentLeave: 0, seniorityLeave: 0, monthlyAccruedLeave: 0, usedLeave: 0,
    joinedDate: new Date().toISOString().split('T')[0],
    remainingLeave: 0,
  });

  useEffect(() => {
    if (show) {
      console.log("🛠 [UserModal] Initializing with data:", initialData); // Check dữ liệu đầu vào
      setKey("account");
      setValidated(false);
      setShowPassword(false);

      if (initialData) {
        const u = initialData; 
        const ld = u.leaveDetail || {}; // An toàn hơn so với initialData.leaveDetail
        
        const deptIds = u.departmentIds 
          ? u.departmentIds 
          : (u.userDepartments ? u.userDepartments.map(ud => ud.departmentId) : []);

        setFormData({
          id: u.id || "",
          userName: u.userName || "",
          fullName: u.employeeName || u.fullName || "",
          email: u.email || "",
          status: u.status || "Active",
          role: u.role || "User",
          branchId: u.branchId ? u.branchId.toString() : "",
          departmentIds: Array.isArray(deptIds) ? deptIds : [], // Đảm bảo luôn là mảng
          position: u.position !== undefined ? u.position : 0,
          employeeCode: u.employeeCode || "",
          positionDescription: u.positionDescription || "",
          workLocation: u.workLocation || "",
          password: PASSWORD_PLACEHOLDER,
          currentLeave: ld.currentLeave ?? 0,
          seniorityLeave: ld.seniorityLeave ?? 0,
          monthlyAccruedLeave: ld.monthlyAccruedLeave ?? 0,
          usedLeave: ld.usedLeave ?? 0,
          joinedDate: ld.joinedDate ? ld.joinedDate.split('T')[0] : new Date().toISOString().split('T')[0],
        });
      } else {
        // Reset về mặc định khi tạo mới
        setFormData({
          id: "", userName: "", fullName: "", email: "", status: "Active", role: "User",
          password: "", branchId: "", departmentIds: [], position: 0,
          employeeCode: "", positionDescription: "", workLocation: "",
          currentLeave: 0, seniorityLeave: 0, monthlyAccruedLeave: 0, usedLeave: 0,
          joinedDate: new Date().toISOString().split('T')[0],
          remainingLeave: 0
        });
      }
    }
  }, [initialData, show]);

  // Logic tính toán số ngày phép còn lại (Giữ nguyên của bạn)
  useEffect(() => {
    const remainingLeave = (Number(formData.currentLeave) || 0) + 
                           (Number(formData.seniorityLeave) || 0) + 
                           (Number(formData.monthlyAccruedLeave) || 0) - 
                           (Number(formData.usedLeave) || 0);
    setFormData(prev => ({ ...prev, remainingLeave }));
  }, [formData.currentLeave, formData.seniorityLeave, formData.monthlyAccruedLeave, formData.usedLeave]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const numericFields = ["position", "currentLeave", "seniorityLeave", "monthlyAccruedLeave", "usedLeave"];
    const finalValue = numericFields.includes(name) ? (value === "" ? 0 : parseFloat(value)) : value;
    setFormData(prev => ({ ...prev, [name]: finalValue }));
  };

  const handleDepartmentChange = (deptId) => {
    setFormData(prev => {
      const currentDepts = Array.isArray(prev.departmentIds) ? prev.departmentIds : [];
      const isSelected = currentDepts.includes(deptId);
      return {
        ...prev,
        departmentIds: isSelected 
          ? currentDepts.filter(id => id !== deptId) 
          : [...currentDepts, deptId]
      };
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const isDeptInvalid = !formData.departmentIds || formData.departmentIds.length === 0;
    const isPasswordInvalid = !formData.id && (!formData.password || formData.password === "");

    if (!formData.userName || !formData.fullName || !formData.branchId || isDeptInvalid || isPasswordInvalid) {
      setKey("account");
      setValidated(true);
      return;
    }

    const sendData = { 
      ...formData, 
      branchId: Number(formData.branchId),
      employeeName: formData.fullName // Map lại cho đúng Backend nếu cần
    };

    if (formData.id && (formData.password === PASSWORD_PLACEHOLDER || !formData.password)) {
      delete sendData.password;
    }
    
    console.log("🚀 [UserModal] Sending data to Save:", sendData);
    onSave(sendData);
  };

  return (
    <Modal show={show} onHide={onClose} centered backdrop="static" size="lg">
      {/* Giữ nguyên phần UI của bạn bên dưới */}
      <Modal.Header closeButton className="bg-primary text-white">
        <Modal.Title>
            <i className={`fas ${formData.id ? "fa-user-edit" : "fa-user-plus"} me-2`}></i>
            {formData.id ? "Edit User Profile" : "Create New User"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-4">
        {serverError && <div className="alert alert-danger py-2 mb-3 small">{typeof serverError === 'string' ? serverError : JSON.stringify(serverError)}</div>}
        <Tabs activeKey={key} onSelect={(k) => setKey(k)} className="mb-3">
          <Tab eventKey="account" title={<span><i className="fas fa-id-card me-1"></i> Account</span>}>
            <Form noValidate validated={validated} className="mt-3">
              <div className="row">
                <div className="col-md-6 mb-3">
                  <Form.Label className="fw-bold">Full Name *</Form.Label>
                  <Form.Control required name="fullName" value={formData.fullName || ""} onChange={handleChange} />
                </div>
                <div className="col-md-6 mb-3">
                  <Form.Label className="fw-bold">Username *</Form.Label>
                  <Form.Control required name="userName" value={formData.userName || ""} onChange={handleChange} />
                </div>
              </div>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <Form.Label className="fw-bold">Employee Code</Form.Label>
                  <Form.Control name="employeeCode" value={formData.employeeCode || ""} onChange={handleChange} />
                </div>
                <div className="col-md-6 mb-3">
                  <Form.Label className="fw-bold">Position Description</Form.Label>
                  <Form.Control name="positionDescription" value={formData.positionDescription || ""} onChange={handleChange} />
                </div>
              </div>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <Form.Label className="fw-bold">Email *</Form.Label>
                  <Form.Control required type="email" name="email" value={formData.email || ""} onChange={handleChange} />
                </div>
                <div className="col-md-6 mb-3">
                  <Form.Label className="fw-bold">Work Location</Form.Label>
                  <Form.Control name="workLocation" value={formData.workLocation || ""} onChange={handleChange} />
                </div>
              </div>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <Form.Label className="fw-bold">Branch *</Form.Label>
                  <Form.Select required name="branchId" value={formData.branchId || ""} onChange={handleChange}>
                    <option value="">Select branch</option>
                    {branches?.map(b => <option key={b.id} value={b.id}>{b.branchName}</option>)}
                  </Form.Select>
                </div>
                <div className="col-md-6 mb-3">
                  <Form.Label className="fw-bold">Position (System)</Form.Label>
                  <Form.Select name="position" value={formData.position ?? 0} onChange={handleChange}>
                    {positions.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                  </Form.Select>
                </div>
              </div>
              <div className="row">
                <div className="col-12 mb-3">
                  <Form.Label className="fw-bold text-primary">Departments *</Form.Label>
                  <div className={`p-3 border rounded ${validated && (!formData.departmentIds || formData.departmentIds.length === 0) ? "border-danger" : ""}`} style={{ maxHeight: "150px", overflowY: "auto", backgroundColor: "#f8f9fa" }}>
                    <div className="row">
                      {departments?.map(d => (
                        <div key={d.id} className="col-md-4 mb-2">
                          <Form.Check 
                            type="checkbox" 
                            id={`dept-${d.id}`} 
                            label={d.name} 
                            checked={formData.departmentIds?.includes(d.id)} 
                            onChange={() => handleDepartmentChange(d.id)} 
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <Form.Label className="fw-bold">Role</Form.Label>
                  <Form.Select name="role" value={formData.role || "User"} onChange={handleChange}>
                    {rolesDefault.map(r => <option key={r} value={r}>{r}</option>)}
                  </Form.Select>
                </div>
                <div className="col-md-6 mb-3">
                  <Form.Label className="fw-bold">Status</Form.Label>
                  <Form.Select name="status" value={formData.status || "Active"} onChange={handleChange}>
                    {statuses.map(s => <option key={s} value={s}>{s}</option>)}
                  </Form.Select>
                </div>
              </div>
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">Password {formData.id ? "(Blank to keep current)" : "*"}</Form.Label>
                <InputGroup>
                  <Form.Control type={showPassword ? "text" : "password"} name="password" value={formData.password || ""} onChange={handleChange} />
                  <Button variant="outline-secondary" onClick={() => setShowPassword(!showPassword)}>
                    <i className={`fas ${showPassword ? "fa-eye-slash" : "fa-eye"}`}></i>
                  </Button>
                </InputGroup>
              </Form.Group>
            </Form>
          </Tab>
          <Tab eventKey="leave" title={<span><i className="fas fa-calendar-check me-1"></i> Leave Info</span>}>
            <div className="row mt-3">
              <div className="col-md-6 mb-3"><Form.Label className="fw-bold">Joined Date</Form.Label><Form.Control type="date" name="joinedDate" value={formData.joinedDate || ""} onChange={handleChange} /></div>
              <div className="col-md-6 mb-3"><Form.Label className="fw-bold">Annual Leave (Days)</Form.Label><Form.Control type="number" name="currentLeave" value={formData.currentLeave ?? 0} onChange={handleChange} /></div>
            </div>
            <div className="row">
              <div className="col-md-6 mb-3"><Form.Label className="fw-bold">Seniority Leave</Form.Label><Form.Control type="number" name="seniorityLeave" value={formData.seniorityLeave ?? 0} onChange={handleChange} /></div>
              <div className="col-md-6 mb-3"><Form.Label className="fw-bold">Monthly Cumulative</Form.Label><Form.Control type="number" name="monthlyAccruedLeave" value={formData.monthlyAccruedLeave ?? 0} readOnly className="bg-light" /></div>
            </div>
            <div className="row">
              <div className="col-md-6 mb-3"><Form.Label className="fw-bold">Days Used</Form.Label><Form.Control type="number" name="usedLeave" value={formData.usedLeave ?? 0} readOnly className="bg-light" /></div>
              <div className="col-md-6 mb-3"><Form.Label className="fw-bold">Remaining Leave</Form.Label><Form.Control type="number" name="remainingLeave" value={formData.remainingLeave ?? 0} readOnly className="bg-light" /></div>
            </div>
          </Tab>
        </Tabs>
      </Modal.Body>
      <Modal.Footer className="bg-light">
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button variant="primary" onClick={handleSubmit} className="px-4">{formData.id ? "Update User" : "Create User"}</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default UserModal;